import type { api } from "@healthos/schemas";
import { CircleNotch } from "@phosphor-icons/react";
import type { useMutation } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction, useState } from "react";
import { previewRouteMilestone } from "../route-milestone-preview";
import { useDemoConfirm } from "./demo-confirm";
import {
  LENS_FILL_OPTIONS,
  type LensFillVariables,
  type LifeContext,
  PERSONA_META,
  type PersonaId,
  personaMeta,
  type RouteContext,
  type WhoamiResponse,
} from "./demo-control-model";
import { PanelSection } from "./demo-control-sections";

const buttonBase =
  "rounded-md border px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55";
const quietButton =
  "border-surface-elevated/45 bg-surface-elevated/55 text-ink-body hover:border-teal-400/60 hover:bg-surface-elevated/85";
const selectedButton = "border-teal-500/70 bg-surface-elevated/90 text-ink-primary shadow-sm";

export function CustomMemberSection({
  data,
  isBusy,
  routeContext,
  seedMutation,
  backfillMutation,
  lensFillMutation,
  fillDays,
  onFillDaysChange,
  fillAllLensesMutation,
  communitySeedMutation,
  tickMutation,
  resetMutation,
  journeyProgressMutation,

  challengesSeedMutation,
  pastChallengesSeedMutation,
  justEndedSeedMutation,
  activateRouteVoteMutation,
  stepHistoryMutation,
  routeWaypointMutation,
}: {
  data: WhoamiResponse | undefined;
  isBusy: boolean;
  routeContext: RouteContext;
  seedMutation: ReturnType<
    typeof useMutation<unknown, Error, { persona: PersonaId; lifeContext: LifeContext }>
  >;
  backfillMutation: ReturnType<typeof useMutation<unknown, Error, void>>;
  lensFillMutation: ReturnType<
    typeof useMutation<api.DevFillLensResponse, Error, LensFillVariables>
  >;
  fillDays: api.DevFillDays;
  onFillDaysChange: (days: api.DevFillDays) => void;
  fillAllLensesMutation: ReturnType<
    typeof useMutation<api.DevFillAllLensesResponse, Error, api.DevFillDays>
  >;
  communitySeedMutation: ReturnType<typeof useMutation<api.CommunityDevSeedResponse, Error, void>>;
  tickMutation: ReturnType<typeof useMutation<api.SimulationProxyResponse, Error, boolean>>;
  resetMutation: ReturnType<typeof useMutation<unknown, Error, void>>;

  journeyProgressMutation: ReturnType<
    typeof useMutation<api.DevJourneyProgressResponse, Error, api.DevJourneyProgressRequest>
  >;
  challengesSeedMutation: ReturnType<
    typeof useMutation<api.DevSeedChallengesResponse, Error, void>
  >;
  pastChallengesSeedMutation: ReturnType<
    typeof useMutation<api.DevSeedPastChallengesResponse, Error, void>
  >;
  justEndedSeedMutation: ReturnType<
    typeof useMutation<api.DevSeedPastChallengesResponse, Error, void>
  >;
  activateRouteVoteMutation: ReturnType<
    typeof useMutation<api.DevActivateCompanyRouteVoteResponse, Error, void>
  >;
  stepHistoryMutation: ReturnType<typeof useMutation<api.DevSeedStepHistoryResponse, Error, void>>;
  routeWaypointMutation: ReturnType<typeof useMutation<api.DevRouteWaypointResponse, Error, void>>;
}) {
  const { confirmDestructive } = useDemoConfirm();
  const highlighted = routeContext === "patient";
  return (
    <PanelSection
      title="Member data"
      eyebrow={highlighted ? "Custom patient controls" : "Patient controls"}
    >
      <p className="mb-2 font-mono text-xs uppercase tracking-[0.14em] text-ink-tertiary">
        Persona
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        {(Object.keys(PERSONA_META) as PersonaId[]).map((p) => {
          const isActive = data?.persona === p;
          return (
            <button
              key={p}
              type="button"
              disabled={isBusy}
              onClick={() => {
                confirmDestructive(`Switch to ${personaMeta(p).short}`, () => {
                  seedMutation.mutate({ persona: p, lifeContext: data?.lifeContext ?? "default" });
                });
              }}
              className={`${buttonBase} ${isActive ? selectedButton : quietButton}`}
            >
              {seedMutation.isPending && seedMutation.variables?.persona === p
                ? "..."
                : personaMeta(p).short}
            </button>
          );
        })}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {(["default", "low_agency"] as LifeContext[]).map((lc) => {
          const isActive = data?.lifeContext === lc;
          return (
            <button
              key={lc}
              type="button"
              disabled={isBusy || !data?.persona}
              onClick={() => {
                const persona = data?.persona;
                if (persona) {
                  confirmDestructive(`Switch to ${lc}`, () => {
                    seedMutation.mutate({ persona, lifeContext: lc });
                  });
                }
              }}
              className={`${buttonBase} ${isActive ? selectedButton : quietButton}`}
              title={
                lc === "low_agency"
                  ? "Survival mode: low agency, no optimization framing"
                  : "Standard optimization framing"
              }
            >
              {lc === "low_agency" ? "Survival" : "Default"}
            </button>
          );
        })}
      </div>
      <div className="mb-1.5 mt-3 flex items-center justify-between gap-2">
        <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-tertiary">Lens data</p>
        <div className="flex gap-1">
          {([6, 14] as const).map((d) => (
            <button
              key={d}
              type="button"
              disabled={isBusy}
              onClick={() => onFillDaysChange(d)}
              className={`rounded-md border px-1.5 py-0.5 text-2xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55 ${
                fillDays === d ? selectedButton : quietButton
              }`}
              title={`Backfill ${d} days of history`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {LENS_FILL_OPTIONS.map((option) => {
          const Icon = option.Icon;
          const running =
            lensFillMutation.isPending && lensFillMutation.variables?.lens === option.id;
          return (
            <button
              key={option.id}
              type="button"
              disabled={isBusy}
              onClick={() => {
                confirmDestructive(option.label, () =>
                  lensFillMutation.mutate({ lens: option.id, days: fillDays }),
                );
              }}
              className="flex min-h-8 items-center justify-center gap-1.5 rounded-md border border-teal-500/45 bg-surface-secondary/60 px-2 py-1.5 text-xs font-semibold text-teal-800 transition-colors hover:bg-teal-100/75 disabled:cursor-not-allowed disabled:border-border-strong/40 disabled:bg-ink-on-media/40 disabled:text-ink-quaternary"
              title={`${option.title}. Switches persona and reseeds the lens's ${fillDays}-day signal window.`}
            >
              {running ? (
                <CircleNotch
                  size={12}
                  weight="bold"
                  className="motion-safe:animate-spin text-ink-brand"
                />
              ) : (
                <Icon size={12} weight="regular" className="shrink-0" />
              )}
              <span className="min-w-0 truncate">{running ? "Filling..." : option.label}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        disabled={isBusy}
        onClick={() => {
          confirmDestructive("Fill all lenses", () => fillAllLensesMutation.mutate(fillDays));
        }}
        className="mt-1.5 flex min-h-8 w-full items-center justify-center gap-1.5 rounded-md border border-teal-500/45 bg-surface-secondary/60 px-2 py-1.5 text-xs font-semibold text-teal-800 transition-colors hover:bg-teal-100/75 disabled:cursor-not-allowed disabled:border-border-strong/40 disabled:bg-ink-on-media/40 disabled:text-ink-quaternary"
        title={`Seeds every lens's ${fillDays}-day signal window in one write (does not touch persona).`}
      >
        {fillAllLensesMutation.isPending ? (
          <CircleNotch
            size={12}
            weight="bold"
            className="motion-safe:animate-spin text-ink-brand"
          />
        ) : null}
        <span>{fillAllLensesMutation.isPending ? "Filling..." : `+${fillDays}d all lenses`}</span>
      </button>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <button
          type="button"
          disabled={isBusy}
          onClick={() => backfillMutation.mutate()}
          className={`${buttonBase} ${quietButton}`}
          title="Insert 7 days of varied wearable signals"
        >
          {backfillMutation.isPending ? "..." : "+7d signals"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => stepHistoryMutation.mutate()}
          className={`${buttonBase} border-teal-500/60 bg-surface-secondary/70 text-teal-800 hover:bg-teal-100/80`}
          title="Populate seven days of synced step and distance totals, including today's goal and streak."
        >
          {stepHistoryMutation.isPending ? "Seeding..." : "Seed step week"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => challengesSeedMutation.mutate()}
          className={`${buttonBase} border-teal-500/60 bg-surface-secondary/70 text-teal-800 hover:bg-teal-100/80`}
          title="Seed a route community with visible teammate markers, groups, leaderboards, and synced activity examples"
        >
          {challengesSeedMutation.isPending ? "Seeding..." : "Seed route community"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => pastChallengesSeedMutation.mutate()}
          className={`${buttonBase} border-teal-500/60 bg-surface-secondary/70 text-teal-800 hover:bg-teal-100/80`}
          title="Seed two ended company challenges with about 100 walkers, a full daily ledger, finishers and final standings"
        >
          {pastChallengesSeedMutation.isPending ? "Seeding..." : "Seed past challenges"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => justEndedSeedMutation.mutate()}
          className={`${buttonBase} border-teal-500/60 bg-surface-secondary/70 text-teal-800 hover:bg-teal-100/80`}
          title="Seed a challenge that ended three days ago, so the just-ended summary card (and any ended alert) shows on the main challenges page"
        >
          {justEndedSeedMutation.isPending ? "Seeding..." : "Seed just-ended"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => activateRouteVoteMutation.mutate()}
          className={`${buttonBase} border-teal-500/60 bg-surface-secondary/70 text-teal-800 hover:bg-teal-100/80`}
          title="Move the active company challenge into its final day so next route voting is visible"
        >
          {activateRouteVoteMutation.isPending ? "Activating..." : "Activate route vote"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => communitySeedMutation.mutate()}
          className={`${buttonBase} ${quietButton}`}
          title="Seed a local synthetic company community"
        >
          {communitySeedMutation.isPending ? "..." : "Seed community"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => tickMutation.mutate(false)}
          className={`${buttonBase} ${quietButton}`}
          title="Run Heartbeat after staging demo signals"
        >
          {tickMutation.isPending ? "..." : "Force tick"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => {
            confirmDestructive("Reset your data and restart onboarding", () => {
              resetMutation.mutate();
            });
          }}
          className={`${buttonBase} border-surface-elevated/40 bg-surface-elevated/50 text-ink-body hover:border-rose-400/60 hover:bg-rose-50/80 hover:text-rose-800`}
          title="Wipe data and route to /onboarding"
        >
          {resetMutation.isPending ? "..." : "Reset"}
        </button>
      </div>
      <JourneyProgressSection isBusy={isBusy} mutation={journeyProgressMutation} />
      <RouteMilestonePreviewSection mutation={routeWaypointMutation} />
    </PanelSection>
  );
}

/**
 * Two ways to see the waypoint celebration.
 *
 * "Replay mine" reopens the furthest waypoint on the member's real active
 * route, and its share button publishes to the community for real, so the whole
 * path can be checked. "Layout" opens a placeholder route instead, for
 * reviewing the visuals when the member has not reached a waypoint yet; it
 * calls no API and posts nothing.
 */
function RouteMilestonePreviewSection({
  mutation,
}: {
  mutation: ReturnType<typeof useMutation<api.DevRouteWaypointResponse, Error, void>>;
}) {
  return (
    <div className="mt-3 border-t border-border-strong/30 pt-3">
      <p className="font-mono text-2xs uppercase tracking-[0.14em] text-ink-tertiary">
        Waypoint celebration
      </p>
      <p className="mt-1 text-2xs leading-snug text-ink-tertiary">
        Replay uses your real route and can post to Community. Layout uses a placeholder and posts
        nothing.
      </p>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          className={`${buttonBase} ${quietButton}`}
          title="Reopen the latest state on your active route"
        >
          {mutation.isPending ? "..." : "Replay mine"}
        </button>
        <button
          type="button"
          onClick={() => previewRouteMilestone("member")}
          className={`${buttonBase} ${quietButton}`}
        >
          Layout: waypoint
        </button>
        <button
          type="button"
          onClick={() => previewRouteMilestone("member", "completion")}
          className={`${buttonBase} ${quietButton}`}
        >
          Layout: complete
        </button>
        <button
          type="button"
          onClick={() => previewRouteMilestone("group", "completion")}
          className={`${buttonBase} ${quietButton}`}
        >
          Layout: group complete
        </button>
      </div>
    </div>
  );
}

function JourneyProgressSection({
  isBusy,
  mutation,
}: {
  isBusy: boolean;
  mutation: ReturnType<
    typeof useMutation<api.DevJourneyProgressResponse, Error, api.DevJourneyProgressRequest>
  >;
}) {
  const [amount, setAmount] = useState(1000);
  const [unit, setUnit] = useState<api.DevJourneyProgressRequest["unit"]>("steps");
  return (
    <div className="mt-3 border-t border-border-strong/30 pt-3">
      <p className="font-mono text-2xs uppercase tracking-[0.14em] text-ink-tertiary">
        Journey progress
      </p>
      <p className="mt-1 text-2xs leading-snug text-ink-tertiary">
        Advances only the active journey. Kilometers map proportionally onto its published route.
      </p>
      <div className="mt-2 flex items-end gap-1.5">
        <label className="min-w-0 flex-1">
          <span className="font-mono text-xs uppercase tracking-[0.1em] text-ink-tertiary">
            Amount
          </span>
          <input
            type="number"
            min={unit === "steps" ? 1 : 0.1}
            max={100000}
            step={unit === "steps" ? 100 : 0.1}
            value={amount}
            onChange={(event) => setAmount(Number(event.target.value))}
            className="mt-0.5 w-full rounded-md border border-border-card bg-surface-elevated/80 px-2 py-1 font-mono text-xs tabular-nums text-ink-primary focus:border-teal-500 focus:outline-none"
          />
        </label>
        <div className="grid grid-cols-2 gap-1 rounded-md bg-surface-track/65 p-0.5">
          {(["steps", "kilometers"] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={unit === value}
              onClick={() => {
                setUnit(value);
                setAmount(value === "steps" ? 1000 : 1);
              }}
              className={`rounded px-2 py-1 font-mono text-xs ${unit === value ? "bg-surface-elevated text-ink-primary shadow-sm" : "text-ink-tertiary"}`}
            >
              {value === "steps" ? "Steps" : "KM"}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={isBusy || !Number.isFinite(amount) || amount <= 0}
          onClick={() => mutation.mutate({ amount, unit })}
          className={`${buttonBase} border-teal-500/60 bg-surface-secondary/70 text-teal-800 hover:bg-teal-100/80`}
        >
          {mutation.isPending ? "..." : "Add"}
        </button>
      </div>
      <div className="mt-1.5 grid grid-cols-4 gap-1">
        {(unit === "steps" ? [1000, 2500, 5000, 10000] : [0.5, 1, 2, 5]).map((value) => (
          <button
            key={value}
            type="button"
            disabled={isBusy}
            onClick={() => mutation.mutate({ amount: value, unit })}
            className={`${buttonBase} ${quietButton}`}
          >
            +{value.toLocaleString()}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CustomOpsSection({
  isBusy,
  routeContext,
  cohortDraft,
  cohortChanged,
  setCohortDraft,
  cohortMutation,
  onResetCohort,
}: {
  isBusy: boolean;
  routeContext: RouteContext;
  cohortDraft: { headcount: number; pmpm: number };
  cohortChanged: boolean;
  setCohortDraft: Dispatch<SetStateAction<{ headcount: number; pmpm: number }>>;
  cohortMutation: ReturnType<typeof useMutation<void, Error, { headcount: number; pmpm: number }>>;
  onResetCohort: () => void;
}) {
  const highlighted = routeContext === "ops";
  return (
    <PanelSection title="Ops cohort" eyebrow={highlighted ? "Cohort context" : "Ops controls"}>
      <div className="mb-1.5 flex justify-end">
        <button
          type="button"
          onClick={onResetCohort}
          className="rounded px-1.5 py-0.5 font-mono text-xs text-ink-quaternary transition-colors hover:bg-surface-track/70 hover:text-ink-body"
        >
          defaults
        </button>
      </div>
      <div className="flex items-end gap-2">
        <CohortInput
          label="Lives"
          value={cohortDraft.headcount}
          min={100}
          max={1_000_000}
          step={100}
          onChange={(v) => setCohortDraft((c) => ({ ...c, headcount: v }))}
        />
        <CohortInput
          label="PMPM ($)"
          value={cohortDraft.pmpm}
          min={100}
          max={5000}
          step={1}
          onChange={(v) => setCohortDraft((c) => ({ ...c, pmpm: v }))}
        />
        <button
          type="button"
          onClick={() => cohortMutation.mutate(cohortDraft)}
          disabled={!cohortChanged || isBusy}
          className="rounded-md border border-teal-500/60 bg-surface-secondary/70 px-2.5 py-1 text-xs font-medium text-teal-800 transition-colors hover:bg-teal-100/80 disabled:cursor-not-allowed disabled:border-border-strong/40 disabled:bg-ink-on-media/40 disabled:text-ink-quaternary"
        >
          {cohortMutation.isPending ? "..." : "Apply"}
        </button>
      </div>
    </PanelSection>
  );
}

function CohortInput({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-1 flex-col gap-0.5">
      <span className="font-mono text-xs uppercase tracking-[0.1em] text-ink-tertiary">
        {label}
      </span>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number.parseInt(e.target.value, 10) || value)}
        className="w-full rounded-md border border-border-card bg-surface-elevated/80 px-2 py-1 text-right font-mono text-xs tabular-nums text-ink-primary focus:border-teal-500 focus:outline-none"
      />
    </label>
  );
}
